import { useCallback, useEffect, useState } from 'react';

/**
 * Per-device "recently used" workflow tools (E13). Deliberately NOT
 * server-side — per-device recency is fine, ships without a migration.
 * Mirrors useWorkflowFavorites' localStorage read/write pattern.
 */
const STORAGE_KEY = 'riskninja-recent-workflows';
const MAX_RECENT = 8;

export interface RecentWorkflowEntry {
  workflowType: string;
  lastUsedAt: string;
}

const readRecent = (): RecentWorkflowEntry[] => {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    if (!Array.isArray(parsed)) return [];
    return parsed.filter(
      (e): e is RecentWorkflowEntry => !!e && typeof e.workflowType === 'string' && typeof e.lastUsedAt === 'string'
    );
  } catch {
    return [];
  }
};

const writeRecent = (entries: RecentWorkflowEntry[]) => {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(entries));
  } catch {
    // Storage full or disabled — silent failure is fine, this is cosmetic.
  }
};

export const useRecentWorkflows = () => {
  const [recent, setRecent] = useState<RecentWorkflowEntry[]>([]);

  useEffect(() => {
    setRecent(readRecent());
  }, []);

  const recordUsage = useCallback((workflowType: string) => {
    setRecent((prev) => {
      const withoutCurrent = prev.filter((e) => e.workflowType !== workflowType);
      const next = [{ workflowType, lastUsedAt: new Date().toISOString() }, ...withoutCurrent].slice(0, MAX_RECENT);
      writeRecent(next);
      return next;
    });
  }, []);

  return { recent, recordUsage };
};
